import clsx from 'clsx';
import { format } from 'date-fns';
import InfoTip from '@/components/InfoTip';
import { aud } from '@/utils/format';
import type { OutstandingPay, OwedDay, OwedStatus } from '@/domain/outstandingPay';
import type { Studio } from '@/domain/types';

/**
 * "Still owed to artists" (W7 n4, approved Day sheet mock): Finance's network
 * view of artist pay not yet handed over, as at the Day sheet's day. Figures come
 * from `outstandingPay` (which reads `artistArrears`, the Ops source); this only lays them out in the
 * Day sheet's panel/table style.
 */

const dayLabel = (dayIso: string): string => format(new Date(`${dayIso}T12:00:00Z`), 'EEE d MMM');

const STATUS_META: Record<OwedStatus, { label: string; pill: string }> = {
  due: { label: 'Due, unsigned', pill: 'pill-blood' },
  deferred: { label: 'Deferred', pill: 'pill-gold' },
};

const TH = 'font-inter text-[9px] uppercase tracking-widest text-cream-faint pb-2 pr-3';

function DayChip({ day }: { day: OwedDay }) {
  return (
    <span
      className={clsx(
        'rounded-md border px-2 py-0.5 font-mono text-[11px] whitespace-nowrap',
        day.escalated ? 'border-blood/60 bg-blood/10 text-blood' : 'border-gold-dim/40 text-cream-soft',
      )}
      data-testid={`owed-day-${day.dayIso}`}
    >
      {dayLabel(day.dayIso)} · {day.ageDays === 0 ? 'today' : `${day.ageDays}d`}
    </span>
  );
}

export default function OutstandingPayPanel({
  owed,
  studios,
}: {
  owed: OutstandingPay;
  studios: Record<string, Studio>;
}) {
  return (
    <div className="panel p-4" data-testid="outstanding-pay">
      <div className="flex items-baseline flex-wrap gap-x-3 gap-y-1 mb-3">
        <p className="label-small-caps text-gold-bright inline-flex items-center gap-1">
          Still owed to artists
          <InfoTip>
            Artist pay not yet handed over, across the network, as at this day: deferred days (written
            arrears, at what is due less anything paid) and days worked but not signed off yet. A day is
            owed once all that artist&apos;s sessions are done and 30 minutes have passed (or the 6pm
            clock-off), so a day still being worked is left out. The same list as Ops &gt; Unpaid artists.
            A day turns red once it is past the arrears threshold.
          </InfoTip>
        </p>
        <span className="font-serif text-cream-soft text-sm">
          as at {dayLabel(owed.asAtDayIso)} · days whose sessions are all done
        </span>
        <span className="flex-1" />
        <span className="font-inter text-[10px] uppercase tracking-widest text-cream-faint">Network total</span>
        <span className="font-mono tabular-nums text-cream text-lg" data-testid="outstanding-pay-total">
          {aud(owed.totalAud)}
        </span>
      </div>
      {owed.rows.length === 0 ? (
        <p className="font-serif text-cream-soft text-sm">Nothing owed to artists as at this day.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left">
                <th className={TH}>Artist</th>
                <th className={TH}>Studio</th>
                <th className={TH}>Owed days</th>
                <th className={TH}>Status</th>
                <th className={clsx(TH, 'pr-0 text-right')}>Owed</th>
              </tr>
            </thead>
            <tbody>
              {owed.rows.map((r) => {
                const meta = STATUS_META[r.status];
                return (
                  <tr
                    key={`${r.studioId}:${r.artistId}`}
                    className="border-t border-gold-dim/20"
                    data-testid={`owed-row-${r.studioId}-${r.artistId}`}
                  >
                    <td className="py-2 pr-3 text-cream">{r.artistName}</td>
                    <td className="py-2 pr-3 text-cream-soft">{studios[r.studioId]?.name ?? r.studioId}</td>
                    <td className="py-2 pr-3">
                      <span className="inline-flex flex-wrap gap-1.5">
                        {r.days.map((d) => (
                          <DayChip key={d.dayIso} day={d} />
                        ))}
                      </span>
                    </td>
                    <td className="py-2 pr-3">
                      <span className={clsx('text-[10px]', meta.pill)}>{meta.label}</span>
                    </td>
                    <td className="py-2 text-right font-mono tabular-nums text-cream">{aud(r.owedAud)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
